import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, ArrowRight, Lock } from "lucide-react";
import { useEffect, useState } from "react";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";
import { toast } from "sonner";

const INTL = {
  CA: { label: "Canada Standard", rate: 12.99, days: "7–10" },
  GB: { label: "UK Standard", rate: 14.99, days: "8–12" },
  AU: { label: "Australia Standard", rate: 16.99, days: "10–14" },
};

function CartLine({ item: i, updateQty, removeItem }) {
  return (
    <div className="flex gap-4" data-testid={`cart-item-${i.product_id}`}>
      <img src={i.image} alt={i.name} className="w-20 h-20 object-contain bg-white" />
      <div className="flex-1">
        <div className="flex justify-between">
          <p className="font-medium text-sm">{i.name}</p>
          <button onClick={() => removeItem(i.key)} className="text-graphite hover:text-pulse text-xs">✕</button>
        </div>
        <p className="font-mono text-[11px] text-graphite mt-1 uppercase tracking-wider">
          {i.size} / {i.color}
        </p>
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center border border-border">
            <button onClick={() => updateQty(i.key, -1)} className="p-1.5 text-chalk/70 hover:text-pulse"><Minus size={13} /></button>
            <span className="w-8 text-center font-mono text-sm">{i.quantity}</span>
            <button onClick={() => updateQty(i.key, 1)} className="p-1.5 text-chalk/70 hover:text-pulse"><Plus size={13} /></button>
          </div>
          <span className="font-mono text-sm">${(i.price * i.quantity).toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}

function ShippingInfo({ country, total }) {
  if (country !== "US") {
    return (
      <p className="font-mono text-[11px] uppercase tracking-wider text-graphite" data-testid="intl-shipping-note">
        {INTL[country].label} — ${INTL[country].rate.toFixed(2)} · {INTL[country].days} business days
      </p>
    );
  }
  return (
    <div data-testid="free-shipping-bar">
      <p className="font-mono text-[11px] uppercase tracking-wider mb-2 text-graphite" data-testid="free-shipping-text">
        {total < 100
          ? <>Spend <span className="text-signal">${(100 - total).toFixed(2)}</span> more for free standard shipping</>
          : <span className="text-signal">You've unlocked free standard shipping</span>}
      </p>
      <div className="h-1 bg-border overflow-hidden">
        <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min(total / 100, 1) * 100}%` }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className={`h-full ${total < 100 ? "bg-chalk" : "bg-signal"}`}
          data-testid="free-shipping-progress" />
      </div>
    </div>
  );
}

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, total } = useCart();
  const [loading, setLoading] = useState(false);
  const [country, setCountry] = useState("US");
  const [pct, setPct] = useState(10);

  useEffect(() => {
    if (!open) return;
    api.get("/site/status").then((r) => setPct(r.data.look_discount_percent ?? 10)).catch(() => {});
  }, [open]);

  const BOTTOM_RE = /shorts|leggings|pants|skirt/i;
  const ACC_RE = /\bhat\b|hydropulse/i;
  const lookDiscount = pct > 0 &&
    items.some((i) => BOTTOM_RE.test(i.name)) &&
    items.some((i) => !BOTTOM_RE.test(i.name) && !ACC_RE.test(i.name));
  const discountValue = lookDiscount ? (total * pct) / 100 : 0;

  const checkout = async () => {
    if (!items.length) return;
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.size, color: i.color })),
        origin_url: window.location.origin,
        country,
      };
      const { data } = await api.post("/payments/checkout", payload);
      if (data.session_id) sessionStorage.setItem("sync_session", data.session_id);
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)} className="fixed inset-0 z-[60] bg-ink/70 backdrop-blur-sm"
            data-testid="cart-overlay" />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 z-[61] h-full w-full sm:w-[440px] bg-ink border-l border-border flex flex-col"
            data-testid="cart-drawer">
            <div className="flex items-center justify-between px-7 h-20 border-b border-border">
              <h3 className="font-anton text-2xl uppercase tracking-wide">Your Bag</h3>
              <button onClick={() => setOpen(false)} data-testid="cart-close" className="text-chalk/70 hover:text-pulse">
                <X size={22} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-7 py-6 space-y-6">
              {items.length === 0 && (
                <p className="text-graphite font-mono text-sm uppercase tracking-widest mt-10">Your bag is empty.</p>
              )}
              {items.map((i) => (
                <CartLine key={i.key} item={i} updateQty={updateQty} removeItem={removeItem} />
              ))}
            </div>

            {items.length > 0 && (
              <div className="border-t border-border px-7 py-6 space-y-5">
                <div className="flex justify-between items-baseline">
                  <span className="eyebrow">Subtotal</span>
                  <span className="font-anton text-3xl" data-testid="cart-total">${total.toFixed(2)}</span>
                </div>
                {lookDiscount && (
                  <div className="space-y-1.5" data-testid="look-discount-note">
                    <div className="flex justify-between font-mono text-[11px] uppercase tracking-wider text-signal">
                      <span>Complete Look — {pct}% off</span>
                      <span data-testid="look-discount-value">-${discountValue.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-mono text-[11px] uppercase tracking-wider text-chalk/80">
                      <span>Total after discount</span>
                      <span data-testid="look-discount-total">${(total - discountValue).toFixed(2)}</span>
                    </div>
                    <p className="font-mono text-[10px] text-graphite normal-case">
                      Applied automatically at checkout — before shipping &amp; tax.
                    </p>
                  </div>
                )}
                <div className="flex items-center justify-between gap-3">
                  <span className="eyebrow">Ship to</span>
                  <select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="ship-country-select"
                    className="bg-ink border border-border font-mono text-xs uppercase tracking-wider px-3 py-2 outline-none focus:border-pulse text-chalk">
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                    <option value="GB">United Kingdom</option>
                    <option value="AU">Australia</option>
                  </select>
                </div>
                <ShippingInfo country={country} total={total} />
                <p className="font-mono text-[10px] uppercase tracking-wider text-graphite leading-relaxed" data-testid="cart-production-note">
                  Made to order — production 3–7 business days, shipping 7–10 business days after production.
                </p>
                <button onClick={checkout} disabled={loading} data-testid="checkout-btn"
                  className="btn-pulse w-full disabled:opacity-60">
                  {loading ? "Redirecting…" : <>Checkout <ArrowRight size={16} /></>}
                </button>
                <p className="flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-wider text-graphite" data-testid="cart-secure-note">
                  <Lock size={11} className="text-signal" /> Secure checkout powered by Stripe
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
